import React, { useCallback } from 'react'
import InputSearchFilter from './InputSearchFilter'
import TagToggleButton from './TagToggleButton'
import { IMapFilterType, IMapFilterState } from '../contexts/mapFilterReducer'
import { IFilterByOption } from '../ui-components/InputSearchFilter'
import { useActiveProjectsQuery } from '../graphql/hooks/projects'

type Project = { id: string; name: string; is_orderable?: boolean }

interface Props {
  filterState: IMapFilterState
  dispatchFilterState: React.Dispatch<{ type: IMapFilterType; payload?: Partial<IMapFilterState> }>
  areProjectsSelected: boolean
  isWithDeleted: boolean
  setIsWithDeleted: React.Dispatch<React.SetStateAction<boolean>>
}

const FilterDropdown: React.FC<Props> = ({ filterState, dispatchFilterState, areProjectsSelected, isWithDeleted, setIsWithDeleted }) => {
  const { data: activeProjects } = useActiveProjectsQuery()

  const isProjectSelected = useCallback(
    (project: Project) => !!filterState.selectedProjects?.some((id) => id === project.id.toString()),
    [filterState.selectedProjects]
  )

  const areAllSelected = filterState.selectedProjects?.length === activeProjects?.length
  const toggleButtonCss = 'mr-2 mb-2 h-fit'
  const isAnyProjectSelected = filterState.selectedProjects?.length > 0

  const onAllProjectClick = useCallback(() => {
    const selectedProjects = areAllSelected ? [] : activeProjects?.map(({ id }) => id.toString())
    dispatchFilterState({ type: IMapFilterType.SET_SELECTED_PROJECTS, payload: { selectedProjects } })
  }, [areAllSelected, dispatchFilterState, activeProjects])

  const handleToggleButtonClick = useCallback(
    (type: IMapFilterType) => () => {
      if (isAnyProjectSelected) {
        dispatchFilterState({ type })
      }
    },
    [dispatchFilterState, isAnyProjectSelected]
  )

  return (
    <div className="flex">
      <InputSearchFilter
        filterByProjectOption={filterState.filterByOption}
        setFilterByProjectOption={(payload: IFilterByOption) =>
          dispatchFilterState({ type: IMapFilterType.SET_FILTER_OPTION, payload: { filterByOption: payload } })
        }
        isWithDeleted={isWithDeleted}
        setIsWithDeleted={setIsWithDeleted}
      >
        <div className={'flex-1'}>
          <TagToggleButton key="projectAll" onClick={onAllProjectClick} customCSS="mr-2 mb-2" selected={areAllSelected}>
            {'Alle'}
          </TagToggleButton>

          {activeProjects?.map((project: Project) => (
            <TagToggleButton
              key={`project-${project.id}`}
              onClick={() => {
                if (filterState.selectedProjects.includes(project.id.toString())) {
                  const selectedProjects = filterState.selectedProjects.filter((proj) => proj !== project.id.toString())
                  dispatchFilterState({ type: IMapFilterType.SET_SELECTED_PROJECTS, payload: { selectedProjects } })
                } else {
                  const selectedProjects = [...filterState.selectedProjects, project.id.toString()]
                  dispatchFilterState({ type: IMapFilterType.SET_SELECTED_PROJECTS, payload: { selectedProjects } })
                }
              }}
              customCSS="mr-2 mb-2"
              selected={isProjectSelected(project)}
            >
              {project.name}
            </TagToggleButton>
          ))}
        </div>
      </InputSearchFilter>

      <div className="flex p-4 flex-wrap content-start w-2/5">
        <TagToggleButton
          key="future"
          onClick={handleToggleButtonClick(IMapFilterType.SET_ONLY_WITH_SCHEDULED_APPOINTMENTS)}
          customCSS={toggleButtonCss}
          selected={filterState.onlyWithScheduledAppointments}
          disabled={!areProjectsSelected}
        >
          {'Termin vereinbart'}
        </TagToggleButton>

        <TagToggleButton
          key="prio"
          onClick={handleToggleButtonClick(IMapFilterType.SET_ONLY_PRIORITY)}
          customCSS={toggleButtonCss}
          selected={filterState.isOnlyPriority}
          disabled={!areProjectsSelected}
        >
          {'Nur Prio'}
        </TagToggleButton>

        <TagToggleButton
          key="open"
          onClick={handleToggleButtonClick(IMapFilterType.SET_OPEN_POTENTIAL)}
          customCSS={toggleButtonCss}
          selected={filterState.isOpenPotential}
          disabled={!areProjectsSelected}
        >
          {'Offenes Potenzial'}
        </TagToggleButton>

        <TagToggleButton
          key="rej"
          onClick={handleToggleButtonClick(IMapFilterType.SET_INCLUDES_TERMINATED_PROJECTS)}
          customCSS={toggleButtonCss}
          selected={filterState.includesTerminatedProjects}
          disabled={!areProjectsSelected}
        >
          {'inkl. Absager'}
        </TagToggleButton>

        <TagToggleButton
          key="cc"
          onClick={handleToggleButtonClick(IMapFilterType.SET_IS_AVAILABLE_FOR_CALLCENTER)}
          customCSS={toggleButtonCss}
          selected={filterState.isAvailableForCallcenter}
          disabled={!areProjectsSelected}
        >
          {'Call Center Available'}
        </TagToggleButton>
      </div>
    </div>
  )
}

export default FilterDropdown
